// Task editor (edit). Lives under the merged Suites page (2026-07 nav
// review): /tasks redirects to /suites, but this URL stays for deep links
// from suite detail + eval pages.
import { useClient } from '@cortex/react-query';
import type { Judge, Suite, Task } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import {
  TaskEditor,
  buildPayload,
  defaultTaskEditorState,
  taskToEditorState,
  validateTask,
  type ITaskEditorState,
} from '../../components/tasks/TaskEditor';
import {
  computeSuiteMembershipChanges,
  suiteIdsContainingTask,
} from '../../components/tasks/suite-membership';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/tasks/$taskId')({ component: TaskDetailPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

function TaskDetailPage() {
  const { taskId } = Route.useParams();
  const client = useClient();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<ITaskEditorState>(defaultTaskEditorState);
  const [populated, setPopulated] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const task = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => client.http.get(`v1/tasks/${taskId}`).json<Task>(),
  });

  // Fetch judges to resolve names for grader cards
  const judges = useQuery({
    queryKey: ['judges'],
    queryFn: () => client.http.get('v1/judges?limit=200').json<{ items: Judge[]; total: number }>(),
  });

  // Suites with their SuiteTask joins — the source of truth for membership
  // (Task.suiteIds is a legacy mirror the runner ignores). Shares the
  // ['suites'] cache key with the Suites page + TaskEditor's checkboxes.
  const suitesQuery = useQuery({
    queryKey: ['suites'],
    queryFn: () => client.http.get('v1/suites?limit=200').json<IListEnvelope<Suite>>(),
  });

  // Initialize form state from server data once (wait for task + suites so
  // the Suite Membership checkboxes reflect the real joins, not the JSONB).
  useEffect(() => {
    if (populated || !task.data || !suitesQuery.data) return;
    setState({
      ...taskToEditorState(task.data, judges.data?.items),
      suiteIds: suiteIdsContainingTask(suitesQuery.data.items, taskId),
    });
    setPopulated(true);
  }, [task.data, judges.data, suitesQuery.data, populated, taskId]);

  const validation = validateTask(state);

  const update = useMutation({
    mutationFn: async () => {
      setSubmitErr(null);
      const updated = await client.http
        .patch(`v1/tasks/${taskId}`, { json: buildPayload(state) })
        .json<Task>();
      // Reconcile the SuiteTask joins with the editor's selection — the
      // joins are what the eval runner actually reads (suite-membership.ts).
      const current = suiteIdsContainingTask(suitesQuery.data?.items ?? [], taskId);
      const changes = computeSuiteMembershipChanges(state.suiteIds, current);
      await Promise.all([
        ...changes.add.map((suiteId) =>
          client.http.put(`v1/suites/${suiteId}/tasks/${taskId}`).json<Suite>(),
        ),
        ...changes.remove.map((suiteId) =>
          client.http.delete(`v1/suites/${suiteId}/tasks/${taskId}`).json<Suite>(),
        ),
      ]);
      return updated;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks'] });
      await qc.invalidateQueries({ queryKey: ['task', taskId] });
      await qc.invalidateQueries({ queryKey: ['suites'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (e) => setSubmitErr((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: () => client.http.delete(`v1/tasks/${taskId}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['tasks'] });
      await qc.invalidateQueries({ queryKey: ['suites'] });
      await nav({ to: routes.SUITES });
    },
  });

  if (task.isLoading || suitesQuery.isLoading) return <Spinner />;
  if (task.error) return <div className="alert alert--danger">{String(task.error)}</div>;
  if (!task.data) return null;

  return (
    <>
      <PageHeader
        title="Edit Task"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <BackButton to={routes.SUITES} label="Suites" />
            {validation.valid && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--status-success)',
                  border: '1px solid var(--status-success)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '3px 8px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                ✓ Valid
              </span>
            )}
            <button
              className="btn btn--ghost btn--sm"
              style={{ color: 'var(--status-danger)' }}
              onClick={() => {
                if (confirm('Delete this task? This cannot be undone.')) deleteMut.mutate();
              }}
            >
              Delete
            </button>
            <button
              className="btn btn--primary btn--sm"
              disabled={!validation.valid || update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? 'Saving…' : saved ? '✓ Saved' : 'Save Task'}
            </button>
          </div>
        }
      />
      {submitErr && (
        <div className="alert alert--danger" style={{ marginBottom: 16 }}>
          {submitErr}
        </div>
      )}
      <TaskEditor state={state} setState={setState} validation={validation} />
    </>
  );
}
